import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import http from '../../../../shared/http'
import { TApi } from '../../../../shared/const'
import { formatVienna } from '../../../../shared/datetime'
import { useIsReadOnly } from '../../../../shared/alumni'
import useDialogDismiss from '../../../../shared/useDialogDismiss'

import Btn from '../../../../UI/Btn'
import Loading from '../../../../UI/Loading'

import { certificateFileName, downloadCertificate } from '../../common/Courses/data'

import styles from './style.module.scss'

/**
 * Eine Junior Company in der Kurs-Auswertung: die Schüler:innen mit ihrem
 * Stand je Modul und — wer fertig ist — der Urkunde.
 */
export default function CompanyDialog({ company, courseId, onClose }) {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [holt, setHolt] = useState(null)
	const readOnly = useIsReadOnly()
	const dismiss = useDialogDismiss(onClose)

	useEffect(() => {
		http.get(TApi.ADMIN_COURSE_REPORT_COMPANY(company.id), { params: { courseId: courseId || undefined } })
			.then(({ data: antwort }) => setData(antwort))
			.catch(() => setData({ students: [] }))
	}, [company.id, courseId])

	const urkunde = completionId => {
		setHolt(completionId)
		downloadCertificate(TApi.ADMIN_COURSE_REPORT_CERTIFICATE(completionId), certificateFileName(company.name)).finally(
			() => setHolt(null)
		)
	}

	return (
		<div className={styles.backdrop} {...dismiss}>
			<div className={styles.dialog} role="dialog" aria-modal="true" aria-label={company.name}>
				<div className={styles.dialogHead}>
					<div>
						<span className={styles.tileLabel}>{t('Junior Company')}</span>
						<h2>{company.name}</h2>
						<span className={styles.tileHint}>
							{company.companyType} · {company.schoolName || '—'}
						</span>
					</div>
					<button type="button" className={styles.close} onClick={onClose} aria-label={t('Close')}>
						✕
					</button>
				</div>

				{!data && <Loading />}
				{data && (data.students || []).length === 0 && <p>{t('No students.')}</p>}
				{data && (data.students || []).length > 0 && (
					<table className={styles.simple}>
						<thead>
							<tr>
								<th>{t('Name')}</th>
								<th>{t('Lessons')}</th>
								{(data.students[0].modules || []).map((modul, index) => (
									<th key={modul.id} title={modul.title}>
										{t('Module {{n}}', { n: index + 1 })}
									</th>
								))}
								<th>{t('Completed')}</th>
							</tr>
						</thead>
						<tbody>
							{data.students.map(person => (
								<tr key={person.id}>
									<td>{person.name}</td>
									<td>
										{person.lessonsDone ?? 0} / {person.lessonsTotal ?? 0}
									</td>
									{(person.modules || []).map(modul => (
										<td key={modul.id}>
											{modul.passed ? '✓' : modul.best ? `${modul.best.correct}/${modul.best.total}` : '—'}
										</td>
									))}
									<td>
										{person.completedAt ? formatVienna(person.completedAt, 'dd.MM.yyyy') : '—'}{' '}
										{person.completionId && !readOnly && (
											<Btn
												theme="minimal"
												size="small"
												disabled={holt === person.completionId}
												onClick={() => urkunde(person.completionId)}
											>
												{t('Certificate')}
											</Btn>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
		</div>
	)
}

CompanyDialog.propTypes = {
	company: PropTypes.object.isRequired,
	courseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onClose: PropTypes.func.isRequired
}
